import axios, { AxiosResponse } from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Try both token names for compatibility
    const token = localStorage.getItem('token') || localStorage.getItem('emis_auth_token');
    if (token) {
      config.headers.Authorization = `Token ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and auto-logout on session expiry
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const isLoginRequest = error.config?.url?.includes('/auth/login');
      if (!isLoginRequest) {
        // Token rejected by server (expired/invalidated) - clear everything
        localStorage.removeItem('token');
        localStorage.removeItem('emis_auth_token');
        localStorage.removeItem('emis_user');
        localStorage.removeItem('emis_last_activity');
        // Avoid a redirect loop if already on the login page
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login?expired=1';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Types
export interface LoginCredentials {
  username: string;
  password: string;
}

export interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  is_active: boolean;
}

export interface Nation {
  id: number;
  code: string;
  name: string;
  iso_code: string;
  regions_count: number;
  created_at: string;
}

export interface Region {
  id: number;
  code: string;
  name: string;
  nation: number;
  nation_name: string;
  districts_count: number;
  created_at: string;
}

export interface District {
  id: number;
  code: string;
  name: string;
  region: number;
  region_name: string;
  nation_name: string;
  wards_count: number;
  schools_count: number;
  created_at: string;
}

export interface School {
  id: number;
  emis_id: string;
  name: string;
  registration_number: string;
  district: number;
  district_name: string;
  ward?: number;
  ward_name?: string;
  school_level: number;
  school_level_name: string;
  school_type: number;
  school_type_name: string;
  school_owner: number;
  school_owner_name: string;
  year_established?: number;
  is_active: boolean;
  address: string;
  phone: string;
  email: string;
  head_teacher_name: string;
  latitude?: number;
  longitude?: number;
  created_at: string;
}

export interface DataYear {
  id: number;
  year: number;
  name: string;
  start_date: string;
  end_date: string;
  is_current: boolean;
  is_active: boolean;
}

// API Services
export const authAPI = {
  login: (credentials: LoginCredentials): Promise<AxiosResponse<{ user: User; token: string }>> =>
    api.post('/auth/login/', credentials),
  
  logout: (): Promise<AxiosResponse<void>> =>
    api.post('/auth/logout/'),
  
  getProfile: (): Promise<AxiosResponse<any>> =>
    api.get('/core/user-profiles/me/'),
};

export const coreAPI = {
  // Nations
  getNations: (): Promise<AxiosResponse<{ results: Nation[] }>> =>
    api.get('/core/nations/'),
  
  getNation: (id: number): Promise<AxiosResponse<Nation>> =>
    api.get(`/core/nations/${id}/`),
  
  getNationHierarchy: (id: number): Promise<AxiosResponse<any>> =>
    api.get(`/core/nations/${id}/hierarchy/`),

  // Regions
  getRegions: (params?: any): Promise<AxiosResponse<{ results: Region[] }>> =>
    api.get('/core/regions/', { params }),
  
  getRegion: (id: number): Promise<AxiosResponse<Region>> =>
    api.get(`/core/regions/${id}/`),

  // Districts
  getDistricts: (params?: any): Promise<AxiosResponse<{ results: District[] }>> =>
    api.get('/core/districts/', { params }),
  
  getDistrict: (id: number): Promise<AxiosResponse<District>> =>
    api.get(`/core/districts/${id}/`),
  
  getDistrictHierarchy: (id: number): Promise<AxiosResponse<any>> =>
    api.get(`/core/districts/${id}/hierarchy/`),

  // Data Years
  getDataYears: (): Promise<AxiosResponse<{ results: DataYear[] }>> =>
    api.get('/core/data-years/'),
  
  getCurrentYear: (): Promise<AxiosResponse<DataYear>> =>
    api.get('/core/data-years/current/'),
  
  setCurrentYear: (id: number): Promise<AxiosResponse<DataYear>> =>
    api.post(`/core/data-years/${id}/set_current/`),
};

export const schoolsAPI = {
  getSchools: (params?: any): Promise<AxiosResponse<{ results: School[]; count: number }>> =>
    api.get('/schools/schools/', { params }),
  
  getSchool: (id: number): Promise<AxiosResponse<School>> =>
    api.get(`/schools/schools/${id}/`),
  
  createSchool: (data: Partial<School>): Promise<AxiosResponse<School>> =>
    api.post('/schools/schools/', data),
  
  updateSchool: (id: number, data: Partial<School>): Promise<AxiosResponse<School>> =>
    api.put(`/schools/schools/${id}/`, data),
  
  deleteSchool: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/schools/schools/${id}/`),
  
  getSchoolLevels: (): Promise<AxiosResponse<{ results: any[] }>> =>
    api.get('/schools/school-levels/'),
  
  getSchoolTypes: (): Promise<AxiosResponse<{ results: any[] }>> =>
    api.get('/schools/school-types/'),
  
  getSchoolOwners: (): Promise<AxiosResponse<{ results: any[] }>> =>
    api.get('/schools/school-owners/'),
};

export const dataImportAPI = {
  uploadFile: (file: File, sourceType: string): Promise<AxiosResponse<any>> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('source_type', sourceType);
    
    return api.post('/data-import/upload/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  getImportJobs: (): Promise<AxiosResponse<{ results: any[] }>> =>
    api.get('/data-import/jobs/'),
  
  getImportJob: (id: number): Promise<AxiosResponse<any>> =>
    api.get(`/data-import/jobs/${id}/`),
  
  startImport: (jobId: number): Promise<AxiosResponse<any>> =>
    api.post(`/data-import/jobs/${jobId}/start/`),
};

export const analyticsAPI = {
  getDashboardStats: (): Promise<AxiosResponse<any>> =>
    api.get('/analytics/dashboard-stats/'),
  
  getSchoolAnalytics: (params?: any): Promise<AxiosResponse<any>> => {
    // Filter out React Query internal parameters
    const cleanParams = params ? 
      Object.fromEntries(
        Object.entries(params).filter(([key]) => 
          !['queryKey', 'signal', 'meta'].includes(key)
        )
      ) : {};
    return api.get('/analytics/schools/', { params: cleanParams });
  },
  
  getEnrollmentTrends: (params?: any): Promise<AxiosResponse<any>> => {
    const cleanParams = params ? 
      Object.fromEntries(
        Object.entries(params).filter(([key]) => 
          !['queryKey', 'signal', 'meta'].includes(key)
        )
      ) : {};
    return api.get('/analytics/enrollment-trends/', { params: cleanParams });
  },
  
  getInfrastructureStats: (params?: any): Promise<AxiosResponse<any>> => {
    const cleanParams = params ? 
      Object.fromEntries(
        Object.entries(params).filter(([key]) => 
          !['queryKey', 'signal', 'meta'].includes(key)
        )
      ) : {};
    return api.get('/analytics/infrastructure/', { params: cleanParams });
  },
};

export const reportsAPI = {
  getReports: (): Promise<AxiosResponse<{ results: any[] }>> =>
    api.get('/reports/'),
  
  generateReport: (reportId: number, params: any): Promise<AxiosResponse<any>> =>
    api.post(`/reports/${reportId}/generate/`, params),
  
  downloadReport: (reportId: number, format: string): Promise<AxiosResponse<Blob>> =>
    api.get(`/reports/${reportId}/download/`, {
      params: { format },
      responseType: 'blob',
    }),
};

export default api;